import { Container, Point, type DestroyOptions, type PointData, type Texture } from "pixi.js";
import { CoinFountainView, SpriteCoin, type FountainBounds, type PresentationTaskRunner, type ViewportStore } from "@slot/presentation";
import { POTS, type PotKind } from "../rules/symbols";
import { COIN_BURST_TUNING } from "../tuning/featureTuning";

/** A spilled coin's width at the fountain's coin scale 1 (design units): the tuning's scales size it from there. */
const COIN_WIDTH = 100;

/**
 * The coins a pot spills as a gem's light reaches it, its impact playing (the user, 2026-10-12: once, like slot-cannon's
 * big-win fountain, with this game's coin): the engine's coin fountain (decision 73), one per pot, thrown from its
 * opening. In the pots' layer, over the pots and behind the board and the plaques: the coins come out of the potion,
 * arc up over the pot and fall back behind the frame, fading out as they fall past the pot. Each is the board's coin,
 * turning as it flies (`SpriteCoin`). On the UI's runner: never work a round waits for, nor one a slam cuts short.
 * Nothing is thrown without the coin's image.
 */
export class PotCoinBursts extends Container {
  private readonly fountains: Readonly<Record<PotKind, CoinFountainView>> | null;
  private readonly thrown: Record<PotKind, number> = { life: 0, multi: 0, grow: 0 };
  private readonly screen: () => { width: number; height: number };
  private readonly corner = new Point();

  public constructor(input: {
    /** The board's coin; null, nothing spills. */
    coin: Texture | null;
    /** Each pot's opening, in this layer's space (the composition's). */
    openings: Readonly<Record<PotKind, PointData>>;
    viewport: ViewportStore;
    ambientRunner: PresentationTaskRunner;
    /** The screen's size in the stage's units (its CSS pixels): where the coins are out of sight. */
    screen: () => { width: number; height: number };
  }) {
    super({ label: "PotCoinBursts" });
    this.eventMode = "none";
    this.screen = input.screen;
    const coin = input.coin;
    if (!coin) {
      this.fountains = null;
      return;
    }
    const fountain = (kind: PotKind): CoinFountainView => {
      const anchor = new Container({ label: `CoinBurstFrom:${kind}` });
      anchor.position.set(input.openings[kind].x, input.openings[kind].y);
      const view = new CoinFountainView({
        createCoin: () => new SpriteCoin({ texture: coin, size: COIN_WIDTH, turnSeconds: COIN_BURST_TUNING.turnSeconds }),
        tuning: COIN_BURST_TUNING.fountain,
        anchor,
        viewport: input.viewport,
        runner: input.ambientRunner,
        bounds: () => this.bounds(),
      });
      view.label = `CoinBurst:${kind}`;
      this.addChild(anchor, view);
      return view;
    };
    this.fountains = { life: fountain("life"), multi: fountain("multi"), grow: fountain("grow") };
  }

  /** How many spills each pot has thrown, and the coins in flight now (the dev hook's read). */
  public get state(): { thrown: Record<PotKind, number>; flying: number } {
    const flying = POTS.reduce((sum, kind) => sum + (this.fountains?.[kind].children.filter((coin) => coin.visible).length ?? 0), 0);
    return { thrown: { ...this.thrown }, flying };
  }

  /** `pot` spills its coins once, from its opening. */
  public burst(pot: PotKind): void {
    const fountains = this.fountains;
    if (!fountains || this.destroyed) return;
    this.thrown[pot] += 1;
    fountains[pot].burst(COIN_BURST_TUNING.coins, COIN_BURST_TUNING.heightScale);
  }

  public override destroy(options?: DestroyOptions): void {
    for (const kind of POTS) this.fountains?.[kind].dispose();
    super.destroy(options);
  }

  /** The screen in this layer's space: its corners, brought in. */
  private bounds(): FountainBounds {
    const { width, height } = this.screen();
    const topLeft = this.toLocal(this.corner.set(0, 0));
    const left = topLeft.x;
    const top = topLeft.y;
    const bottomRight = this.toLocal(this.corner.set(width, height));
    return { left, top, right: bottomRight.x, bottom: bottomRight.y };
  }
}
